import * as LiveSessionEvents from "../constants/liveSessionEvents";
import * as LiveSessionStates from "../constants/liveSessionStates";
import * as InitialStates from "../constants/initialStates";
import {
  formatLiveSessionDate,
  formatLiveSessionDuration,
  formatLiveSessionScheduledAt,
} from "../utils/dateUtils";

/**
 * Checks if the state is finished
 * @param {*} state
 */
const isFinished = (state) => state === LiveSessionStates.FINISHED;

const isLiveSessionFinished = (liveSession) => isFinished(liveSession?.aasmState);

const isLiveSessionDomainFinished = (liveSession) => isFinished(liveSession?.state);

/**
 * Map LiveChannel Data
 * @param {*} data
 */
export const mapLiveChannelData = ({ data } = {}, previousData) => {
  const liveChannelAttr = data?.attributes;
  if (!liveChannelAttr) {
    return previousData;
  }

  return {
    streamKeyValue: liveChannelAttr?.streamKeyValue,
    ingestEndpoint: liveChannelAttr?.ingestEndpoint,
    playbackUrl: liveChannelAttr?.playbackUrl,
  };
};

/**
 * Map WebSocket Channel Data
 * @param {*} data
 */
const mapWebSocketChannel = ({ channelName, presenceChannelName } = {}, previousData) => {
  if (!channelName || !presenceChannelName) {
    return previousData;
  }

  return {
    channelName,
    presenceChannelName,
  };
};

/**
 * Map WebSocket Data
 * @param {*} data
 */
export const mapWebSocketData = ({ data } = {}, previousData) => {
  const webSocketAttr = data?.attributes;
  if (!webSocketAttr) {
    return previousData;
  }

  const { authKey, stateChannel, chatChannel } = webSocketAttr || {};
  return {
    authKey,
    stateChannel: mapWebSocketChannel(stateChannel),
    chatChannel: mapWebSocketChannel(chatChannel),
  };
};

/**
 * Map WebSocket Data
 * @param {*} data
 */
export const mapPnWebSocketsData = ({ authKey, stateChannel, chatChannel } = {}, previousData) => {
  if (!authKey || !stateChannel || !chatChannel) {
    return previousData;
  }

  return {
    authKey,
    stateChannel: mapWebSocketChannel(stateChannel),
    chatChannel: mapWebSocketChannel(chatChannel),
  };
};

/**
 * Map Recording Data
 * @param {*} data
 */
export const mapRecordingData = ({ url } = {}, previousData) => {
  if (!url) {
    return previousData;
  }

  return {
    url,
  };
};

/**
 * Map Recording Data
 * @param {*} data
 */
export const mapStreamingData = ({ playerToken, playbackUrl } = {}, previousData) => {
  if (!playerToken || !playbackUrl) {
    return previousData;
  }

  return {
    playerToken,
    playbackUrl,
  };
};

/**
 * Map BE LiveSession object to State
 * @param {*} data
 */
export const mapLiveSessionData = ({ data } = {}, previousData) => {
  const { liveChannel, pnWebsockets, recording = {}, ...liveSession } = data?.attributes || {};
  return {
    liveSession: {
      id: liveSession?.id,
      title: liveSession?.title,
      aasmState: liveSession?.aasmState,
      description: liveSession?.description,
      language: liveSession?.language,
      playerToken: liveSession?.playerToken,
      startedAt: liveSession?.startedAt,
      scheduledAt: liveSession?.scheduledAt,
      updatedAt: liveSession?.updatedAt,
      hasRecording: liveSession?.hasRecording,
      isFinished: isLiveSessionFinished(liveSession),
    },
    liveChannel: mapLiveChannelData(liveChannel || {}, previousData?.liveChannel),
    webSocket: mapWebSocketData(pnWebsockets || {}, previousData?.webSocket),
    recording,
  };
};

/**
 * Map BE LiveSessionDomain object to State
 * @param {*} data
 */
export const mapLiveSessionDomainData = (data = {}, previousData) => {
  const { streaming = {}, pnWebsockets = {}, recording = {}, ...liveSession } = data || {};

  return {
    liveSession: {
      id: liveSession?.id,
      title: liveSession?.title,
      aasmState: liveSession?.state,
      description: liveSession?.description,
      language: liveSession?.language,
      startedAt: liveSession?.startedAt,
      scheduledAt: liveSession?.scheduledAt,
      updatedAt: liveSession?.updatedAt,
      hasRecording: liveSession?.hasRecording,
      isFinished: isLiveSessionFinished(liveSession),
      formattedScheduledAt: formatLiveSessionScheduledAt(liveSession?.scheduledAt),
    },
    streaming: mapStreamingData(streaming || {}, previousData?.streaming),
    recording: mapRecordingData(recording || {}, previousData?.recording),
    pnWebsockets: mapPnWebSocketsData(pnWebsockets || {}, previousData?.pnWebsockets),
  };
};

/**
 * Map BE LiveSessions List to State
 * @param {*} data
 */
export const mapLiveSessionsListData = (data, stateList = []) => {
  return {
    liveSessionsList: stateList.concat(
      data?.length
        ? data.map((liveSession) => ({
            id: liveSession?.id,
            title: liveSession?.title,
            language: liveSession?.language,
            aasmState: liveSession?.state,
            updatedAt: liveSession?.updatedAt,
            startedAt: liveSession?.startedAt,
            finishedAt: liveSession?.finishedAt,
            formattedStartedAt: formatLiveSessionDate(liveSession?.startedAt),
            formattedDuration: formatLiveSessionDuration(
              liveSession?.startedAt,
              liveSession?.finishedAt
            ),
            isFinished: isLiveSessionDomainFinished(liveSession),
          }))
        : []
    ),
  };
};

const LiveSessionStateChangeSuccess = (stateChangeEvent) => {
  const stateChangeMapper = {
    [LiveSessionEvents.READY]: {
      readyStateSuccess: true,
    },
    [LiveSessionEvents.STREAMING]: {
      streamingStateSuccess: true,
    },
    [LiveSessionEvents.FINISH]: {
      finishedStateSuccess: true,
    },
    [LiveSessionEvents.CANCEL]: {
      cancelledStateSuccess: true,
      liveSession: InitialStates.getInitialLiveSessionState().liveSession,
    },
  };
  return stateChangeMapper[stateChangeEvent];
};

/**
 * Map BE LiveSession Object to State for LS State Changes
 * @param {*} data
 * @param {*} stateChangeEvent
 * @param {*} state
 */
export const mapLiveSessionStateChange = ({ data, stateChangeEvent, state }) => {
  return {
    ...state,
    liveSession: { ...state?.liveSession, aasmState: data?.aasmState, startedAt: data?.startedAt },
    liveSessionUpdated: true,
    loading: false,
    ...LiveSessionStateChangeSuccess(stateChangeEvent),
  };
};
